import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Barcode from 'react-barcode';
import QRCode from 'qrcode.react';
import html2canvas from 'html2canvas';
import { API_URL } from '../config/constants';

interface OrderData {
  orderNumber: string;
  orderDate: string;
  totalAmount: number;
  status: string;
}

const OrderSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [barcodeUrl, setBarcodeUrl] = useState<string>('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const barcodeRef = useRef<HTMLDivElement>(null);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const combinedRef = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);

  const INITIAL_CODE = '868138';

  const calculateCheckDigit = (code: string): number => {
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      const digit = parseInt(code[i]);
      sum += (i % 2 === 0) ? digit : digit * 3;
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    return checkDigit;
  };

  const generateBarcode = (orderNumber: string): string => {
    const code = orderNumber.slice(-6);
    const code12 = INITIAL_CODE + code;
    const checkDigit = calculateCheckDigit(code12);
    return code12 + checkDigit;
  };

  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const orderNumber = searchParams.get('orderNumber');
        
        if (!orderNumber) {
          console.error('Sipariş numarası bulunamadı');
          return;
        }

        const orderResponse = await fetch(`${API_URL}/api/orders/number/${orderNumber}`);
        if (!orderResponse.ok) throw new Error('Sipariş bilgileri alınamadı');
        const orderResult = await orderResponse.json();
        setOrderData(orderResult);
      } catch (error) {
        console.error('Veri alma hatası:', error);
      }
    };

    fetchOrderData();
  }, [location]);

  const handleDownloadCombined = async () => {
    if (!orderData || !combinedRef.current) return;
    setPrinting(true);

    try {
      const canvas = await html2canvas(combinedRef.current, {
        scale: 3,
        backgroundColor: '#FFFFFF'
      });
      
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `siparis-${orderData.orderNumber}-barkodlar.png`;
      link.href = url;
      link.click();
    } catch (error) {
      console.error('Barkod indirme hatası:', error);
      alert('Barkodlar indirilirken bir hata oluştu');
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintBarcode = async () => {
    if (!orderData || !combinedRef.current) return;
    setPrinting(true);

    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Yazdırma penceresi açılamadı');
      }

      const canvas = await html2canvas(combinedRef.current, {
        scale: 2,
        backgroundColor: '#FFFFFF'
      });

      printWindow.document.write(`
        <html>
          <head>
            <title>Barkod Yazdır - ${orderData.orderNumber}</title>
            <style>
              body { margin: 0; display: flex; justify-content: center; align-items: center; height: 100vh; }
              img { max-width: 100%; height: auto; }
              @media print {
                body { margin: 0; padding: 0; }
                img { max-width: 100%; }
              }
            </style>
          </head>
          <body>
            <img src="${canvas.toDataURL()}" />
            <script>
              window.onload = function() {
                window.print();
                window.onafterprint = function() {
                  window.close();
                }
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      console.error('Barkod yazdırma hatası:', error);
      alert('Barkod yazdırılırken bir hata oluştu');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-md p-8 mb-8">
          <div className="text-center">
            <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Sipariş Başarıyla Oluşturuldu!</h2>
            <p className="text-gray-600 mb-8">Siparişiniz başarıyla sisteme kaydedildi.</p>

            {/* Sipariş Detayları */}
            {orderData && (
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Sipariş Detayları</h3>
                <div className="grid grid-cols-2 gap-4 text-left">
                  <div>
                    <p className="text-sm text-gray-600">Sipariş Numarası:</p>
                    <p className="font-medium">{orderData.orderNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Sipariş Tarihi:</p>
                    <p className="font-medium">
                      {new Date(orderData.orderDate).toLocaleDateString('tr-TR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Toplam Tutar:</p>
                    <p className="font-medium">{orderData.totalAmount.toLocaleString('tr-TR')} ₺</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Durum:</p>
                    <p className="font-medium">{orderData.status}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Barkod ve QR Kod */}
            {orderData && (
              <>
                <div ref={combinedRef} className="bg-white p-8 rounded-xl border-2 border-gray-200 mb-8">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Sipariş #{orderData.orderNumber}</h2>
                    <p className="text-gray-600 text-sm mt-2">
                      {new Date(orderData.orderDate).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="flex flex-col items-center p-6 border border-gray-200 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Barkod</h3>
                      <div className="bg-white p-4">
                        <Barcode
                          value={generateBarcode(orderData.orderNumber)}
                          width={2}
                          height={100}
                          displayValue={true}
                          margin={10}
                          background="#FFFFFF"
                          lineColor="#000000"
                          fontSize={14}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col items-center p-6 border border-gray-200 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">QR Kod</h3>
                      <div className="bg-white p-4">
                        <QRCode
                          value={`http://localhost:3000/order-detail/${orderData.orderNumber}`}
                          size={150}
                          level="H"
                          includeMargin={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* İşlem Butonları */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  <button
                    onClick={handleDownloadCombined}
                    disabled={printing}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {printing ? 'İndiriliyor...' : 'Barkodları İndir'}
                  </button>

                  <button
                    onClick={handlePrintBarcode}
                    disabled={printing}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    {printing ? 'Yazdırılıyor...' : 'Barkodları Yazdır'}
                  </button>
                </div>

                {/* Menü Kartları */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Siparişi Görüntüle */}
                  <div 
                    onClick={() => navigate(`/order-detail/${orderData.orderNumber}`)}
                    className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-200"
                  >
                    <div className="bg-blue-100 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Sipariş Detayları</h3>
                    <p className="text-gray-600 text-sm">Siparişinizin tüm detaylarını görüntüleyin</p>
                  </div>

                  {/* Yeni Sipariş */}
                  <div 
                    onClick={() => navigate('/create-order')}
                    className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-200"
                  >
                    <div className="bg-green-100 rounded-lg w-12 h-12 flex items-center justify-center mb-4">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Yeni Sipariş</h3>
                    <p className="text-gray-600 text-sm">Yeni bir sipariş oluşturun</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess; 